// Service 22 — ที่ปรึกษากฎหมายสำหรับชาวต่างชาติ (สัญญา / แรงงาน / ข้อพิพาท)
// อ้างอิงหลัก: ประมวลกฎหมายแพ่งและพาณิชย์ (ป.พ.พ.) และประมวลกฎหมายวิธีพิจารณาความแพ่ง (ป.วิ.พ.)
// ผ่านสำนักงานคณะกรรมการกฤษฎีกา (krisdika.go.th),
// พ.ร.บ.คุ้มครองแรงงาน พ.ศ. 2541 และกรมสวัสดิการและคุ้มครองแรงงาน (labour.go.th),
// พ.ร.บ.จัดตั้งศาลแรงงานและวิธีพิจารณาคดีแรงงาน พ.ศ. 2522 — ศาลแรงงาน (labourcourt.coj.go.th),
// สำนักงานศาลยุติธรรม (coj.go.th), พ.ร.บ.อนุญาโตตุลาการ พ.ศ. 2545,
// สถาบันอนุญาโตตุลาการ THAC (thac.or.th) และสถาบันอนุญาโตตุลาการ TAI (tai.coj.go.th),
// พ.ร.บ.การประกอบธุรกิจของคนต่างด้าว พ.ศ. 2542 (dbd.go.th),
// พ.ร.บ.ทนายความ พ.ศ. 2528 — สภาทนายความในพระบรมราชูปถัมภ์ (lawyerscouncil.or.th),
// กรมการกงสุล (consular.mfa.go.th) และ HCCH Apostille (มีผลกับไทย 28 กุมภาพันธ์ 2570)
// Client-safe definitions only — no heavy FAQ payload here.

export type LegalFaqCategory =
  | "contract-drafting"
  | "employment-labour"
  | "lease-property-dispute"
  | "shareholder-company"
  | "litigation-court"
  | "arbitration-mediation"
  | "criminal-complaint"
  | "legal-doc-certification";

export interface LegalCategoryDef {
  slug: LegalFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const LEGAL_FAQ_CATEGORIES: LegalCategoryDef[] = [
  {
    slug: "contract-drafting",
    titleTh: "ร่างและตรวจสัญญาสองภาษาสำหรับชาวต่างชาติ",
    titleEn: "Drafting and reviewing bilingual contracts",
    descTh:
      "หลักเสรีภาพในการทำสัญญาตาม ป.พ.พ. ข้อกำหนดภาษาที่ใช้บังคับ กฎหมายที่ใช้บังคับและเขตอำนาจศาล เบี้ยปรับ มัดจำ และเงื่อนไขเลิกสัญญาที่ศาลไทยบังคับได้จริง",
    descEn:
      "Freedom of contract under the Civil and Commercial Code, controlling-language clauses, governing law and jurisdiction, penalties, deposits, and termination terms that Thai courts will actually enforce.",
    matchTh: /สัญญา|ภาษาที่ใช้บังคับ|เบี้ยปรับ|มัดจำ|เลิกสัญญา|ร่าง/,
    matchEn: /contract|clause|governing law|penalt|deposit|terminat|draft/i,
  },
  {
    slug: "employment-labour",
    titleTh: "สัญญาจ้างและกฎหมายแรงงานไทย",
    titleEn: "Employment contracts and Thai labour law",
    descTh:
      "พ.ร.บ.คุ้มครองแรงงาน พ.ศ. 2541 ค่าชดเชยตามมาตรา 118 การบอกกล่าวล่วงหน้า ทดลองงาน วันหยุดพักผ่อน ข้อบังคับการทำงาน และการเลิกจ้างที่ไม่เป็นธรรม",
    descEn:
      "The Labour Protection Act B.E. 2541, statutory severance under section 118, notice periods, probation, annual leave, work rules, and unfair-dismissal claims.",
    matchTh: /แรงงาน|ค่าชดเชย|เลิกจ้าง|ลูกจ้าง|นายจ้าง|ทดลองงาน|มาตรา 118/,
    matchEn: /labour|labor|employ|severance|dismiss|probation|notice period/i,
  },
  {
    slug: "lease-property-dispute",
    titleTh: "สัญญาเช่าและข้อพิพาทอสังหาริมทรัพย์",
    titleEn: "Leases and property disputes",
    descTh:
      "การเช่าเกินสามปีที่ต้องจดทะเบียนตาม ป.พ.พ. มาตรา 538 สัญญาจะซื้อจะขายคอนโด การคืนเงินดาวน์ ข้อพิพาทกับนิติบุคคลอาคารชุด และการโอน ณ สำนักงานที่ดิน",
    descEn:
      "Leases over three years requiring registration under section 538, condominium purchase agreements, deposit refunds, disputes with juristic persons, and transfers at the Land Office.",
    matchTh: /เช่า|คอนโด|อาคารชุด|ที่ดิน|เงินดาวน์|โอนกรรมสิทธิ์|538/,
    matchEn: /lease|rent|condominium|land office|deposit refund|developer/i,
  },
  {
    slug: "shareholder-company",
    titleTh: "สัญญาระหว่างผู้ถือหุ้นและความเสี่ยงเรื่องนอมินี",
    titleEn: "Shareholder agreements and nominee risk",
    descTh:
      "พ.ร.บ.การประกอบธุรกิจของคนต่างด้าว พ.ศ. 2542 สัดส่วนผู้ถือหุ้น อำนาจกรรมการ สัญญาระหว่างผู้ถือหุ้น หุ้นบุริมสิทธิ และความรับผิดของการถือหุ้นแทน",
    descEn:
      "The Foreign Business Act B.E. 2542, shareholding ratios, director authority, shareholder agreements, preference shares, and the liability attached to nominee shareholding.",
    matchTh: /ผู้ถือหุ้น|นอมินี|กรรมการ|ธุรกิจของคนต่างด้าว|หุ้นบุริมสิทธิ/,
    matchEn: /shareholder|nominee|director|foreign business act|preference share/i,
  },
  {
    slug: "litigation-court",
    titleTh: "ฟ้องคดีในศาลไทย อายุความ และการบังคับคดี",
    titleEn: "Thai court litigation, prescription and enforcement",
    descTh:
      "อายุความตาม ป.พ.พ. มาตรา 193/30 และ 448 ค่าขึ้นศาลตาม ป.วิ.พ. การใช้ล่ามและคำแปลในศาล ขั้นตอนอุทธรณ์ฎีกา และการบังคับคดีตามคำพิพากษา",
    descEn:
      "Prescription periods under sections 193/30 and 448, court fees under the Civil Procedure Code, interpreters and translations in court, appeals, and enforcement of judgments.",
    matchTh: /ศาล|อายุความ|ฟ้อง|ค่าขึ้นศาล|บังคับคดี|อุทธรณ์|ฎีกา/,
    matchEn: /court|prescription|limitation|lawsuit|court fee|enforce|appeal/i,
  },
  {
    slug: "arbitration-mediation",
    titleTh: "อนุญาโตตุลาการและการไกล่เกลี่ยข้อพิพาท",
    titleEn: "Arbitration and mediation",
    descTh:
      "พ.ร.บ.อนุญาโตตุลาการ พ.ศ. 2545 ข้อสัญญาอนุญาโตตุลาการ สถาบัน THAC และ TAI การบังคับคำชี้ขาดต่างประเทศตามอนุสัญญานิวยอร์ก และการไกล่เกลี่ยก่อนฟ้อง",
    descEn:
      "The Arbitration Act B.E. 2545, arbitration clauses, the THAC and TAI institutions, enforcing foreign awards under the New York Convention, and pre-litigation mediation.",
    matchTh: /อนุญาโตตุลาการ|ไกล่เกลี่ย|คำชี้ขาด|นิวยอร์ก|THAC|TAI/,
    matchEn: /arbitrat|mediat|award|new york convention|THAC|TAI/i,
  },
  {
    slug: "criminal-complaint",
    titleTh: "แจ้งความ คดีอาญา และการคุ้มครองชาวต่างชาติ",
    titleEn: "Police reports, criminal cases and protection for foreigners",
    descTh:
      "การร้องทุกข์ต่อพนักงานสอบสวนตาม ป.วิ.อ. คดีความผิดต่อส่วนตัวและอายุความร้องทุกข์สามเดือน หมิ่นประมาท ฉ้อโกง การประกันตัว และสิทธิได้ล่ามในชั้นสอบสวน",
    descEn:
      "Filing a complaint with an inquiry official under the Criminal Procedure Code, the three-month window for compoundable offences, defamation, fraud, bail, and the right to an interpreter.",
    matchTh: /แจ้งความ|อาญา|ร้องทุกข์|หมิ่นประมาท|ฉ้อโกง|ประกันตัว|พนักงานสอบสวน/,
    matchEn: /police report|criminal|complaint|defamation|fraud|bail|inquiry official/i,
  },
  {
    slug: "legal-doc-certification",
    titleTh: "รับรองเอกสารทางกฎหมายเพื่อใช้ในไทยและต่างประเทศ",
    titleEn: "Certifying legal documents for use in Thailand and abroad",
    descTh:
      "การรับรองลายมือชื่อโดยทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney) การแปลรับรอง นิติกรณ์กรมการกงสุล และ Apostille ที่จะมีผลกับไทยวันที่ 28 กุมภาพันธ์ 2570",
    descEn:
      "Signature certification by a Notarial Services Attorney, certified translation, MFA legalisation, and the Apostille regime that takes effect for Thailand on 28 February 2027.",
    matchTh: /รับรองลายมือชื่อ|โนตารี|นิติกรณ์|แปล|Apostille|กงสุล/,
    matchEn: /notar|legalis|legaliz|apostille|certified translation|consular/i,
  },
];

export function getLegalCategoryDef(slug: string): LegalCategoryDef | undefined {
  return LEGAL_FAQ_CATEGORIES.find((c) => c.slug === slug);
}